/**
 * Playlists: the loop a screen plays, and the preview that says what it will look
 * like.
 *
 * The page holds the draft item list rather than the editor, for one reason: the
 * preview underneath is fed from the same draft, so a duration typed into a row
 * changes what is on the simulated TV without anything being saved first. That is
 * the whole point of having a preview in the panel — you find out that six
 * seconds is too short for a menu board here, not after driving to Te Puke.
 *
 * Drafts are kept per playlist and are never thrown away on their own: not by a
 * background refetch, and not by clicking onto another playlist and back. Losing
 * somebody's reorder to a list poll that happened to land mid-edit is the kind of
 * bug people stop trusting a tool over, and the fix costs one object.
 */

import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PlaylistUpsert } from '@pbtv/shared';
import { PageHeader } from '../components/PageHeader.js';
import { Modal } from '../components/Modal.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { ErrorBlock, InfoNote, InlineEmpty, LoadingBlock } from '../components/Feedback.js';
import { PlaylistEditor } from '../components/playlists/PlaylistEditor.js';
import { PlaylistForm } from '../components/playlists/PlaylistForm.js';
import type { PlaylistFormValue } from '../components/playlists/PlaylistForm.js';
import { MediaPicker } from '../components/playlists/MediaPicker.js';
import {
  draftItemFor,
  itemsEqual,
  toDraftItems,
  toPlaylistItems,
} from '../components/playlists/runtime.js';
import type { DraftItem } from '../components/playlists/runtime.js';
import { ScreenPreviewPanel } from '../components/preview/ScreenPreviewPanel.js';
import type { DraftPreviewSource } from '../components/preview/ScreenPreviewPanel.js';
import { errorMessage } from '../lib/api.js';
import { canReachStore, hasNoScope, isAdmin } from '../lib/access.js';
import { formatRelativeTime } from '../lib/format.js';
import { listMedia, mediaScopeQueryKey } from '../lib/media.js';
import type { MediaAsset } from '../lib/media.js';
import {
  PLAYLISTS_QUERY_KEY,
  createPlaylist,
  deletePlaylist,
  listPlaylistsAcross,
  saveIssues,
  updatePlaylist,
} from '../lib/playlists.js';
import type { PlaylistRecord } from '../lib/playlists.js';
import { STORES_QUERY_KEY, listStores } from '../lib/stores.js';
import { useSession } from '../lib/session.js';
import { useNow } from '../lib/useNow.js';
import '../components/playlists/playlists.css';

/** Edits in progress, keyed by playlist id. */
type Drafts = Readonly<Record<string, readonly DraftItem[]>>;

type Dialog =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'rename'; playlist: PlaylistRecord }
  | { kind: 'delete'; playlist: PlaylistRecord }
  | { kind: 'picker' };

export function PlaylistsPage(): ReactElement {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const now = useNow();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Drafts>({});
  const [dialog, setDialog] = useState<Dialog>({ kind: 'none' });
  const closeDialog = (): void => setDialog({ kind: 'none' });

  const stores = useQuery({
    queryKey: STORES_QUERY_KEY,
    queryFn: ({ signal }) => listStores(signal),
  });

  const writableStores = useMemo(
    () => (stores.data ?? []).filter((store) => canReachStore(user, store.storeId)),
    [stores.data, user],
  );

  // The API lists one library per request, so the panel asks for the shared one
  // and for each store this account reaches. That is more requests than a single
  // "give me everything" would be, and it is also why a manager can never be
  // served a row from a store they do not hold.
  const scopes = useMemo<(string | null)[]>(
    () => [null, ...writableStores.map((store) => store.storeId)],
    [writableStores],
  );

  const playlists = useQuery({
    queryKey: [...PLAYLISTS_QUERY_KEY, 'reachable', scopes.map((scope) => scope ?? '').join('|')],
    queryFn: ({ signal }) => listPlaylistsAcross(scopes, signal),
    enabled: !stores.isPending && !stores.isError,
  });

  // Shared playlists are written by admins only; a store's own are written by
  // anyone who holds that store. The server decides this again on every request —
  // this is only here so the panel does not offer a button that must fail.
  const canWrite = (playlist: PlaylistRecord): boolean =>
    playlist.storeId === null ? isAdmin(user) : canReachStore(user, playlist.storeId);

  const rows = useMemo(() => sortPlaylists(playlists.data ?? []), [playlists.data]);
  const selected = rows.find((row) => row.playlistId === selectedId) ?? null;
  const writable = selected === null ? false : canWrite(selected);

  // A draft is seeded the first time its playlist is opened and never reseeded
  // behind somebody's back. A later refetch of the same playlist is not a reason
  // to discard work in progress, and the save replaces the whole list anyway.
  useEffect(() => {
    if (selected === null) return;
    setDrafts((current) =>
      current[selected.playlistId] === undefined
        ? { ...current, [selected.playlistId]: toDraftItems(selected.items) }
        : current,
    );
  }, [selected]);

  const draftRows = selectedId === null ? undefined : drafts[selectedId];
  const scopeStoreId = selected?.storeId ?? null;

  const setDraftRows = (items: readonly DraftItem[]): void => {
    if (selectedId === null) return;
    setDrafts((current) => ({ ...current, [selectedId]: items }));
  };

  // One request, not two merged here.
  //
  // Asking for a store's library already returns that store's own media PLUS the
  // shared assets whose brand tags reach it. Fetching the shared library
  // separately and merging it in would add back the assets the server had just
  // filtered out — which is how a Rosetown Kebab promo ends up offered for a
  // Pizza Bros screen. The brand rule belongs on the server, where it is applied
  // once, rather than being re-derived here from data the client does not have.
  //
  // The cache key matches the media page's, so browsing the library and then
  // opening the picker costs nothing, and an upload invalidating the media
  // prefix sweeps both.
  const pickerMedia = useQuery({
    queryKey: mediaScopeQueryKey(scopeStoreId),
    queryFn: ({ signal }) => listMedia(scopeStoreId, signal),
    enabled: selected !== null,
  });

  const assets = useMemo(() => pickerMedia.data ?? [], [pickerMedia.data]);

  const draftItems = useMemo(
    () => (draftRows === undefined ? [] : toPlaylistItems(draftRows)),
    [draftRows],
  );

  const dirty =
    selected !== null && draftRows !== undefined && !itemsEqual(draftItems, selected.items);

  const save = useMutation({
    mutationFn: ({ playlist, items }: { playlist: PlaylistRecord; items: PlaylistUpsert['items'] }) =>
      updatePlaylist(playlist.playlistId, {
        name: playlist.name,
        storeId: playlist.storeId,
        brands: playlist.brands,
        items,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PLAYLISTS_QUERY_KEY });
    },
  });

  const create = useMutation({
    mutationFn: (value: PlaylistFormValue) =>
      createPlaylist({ name: value.name, storeId: value.storeId, brands: value.brands, items: [] }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: PLAYLISTS_QUERY_KEY });
      if (created !== null) setSelectedId(created.playlistId);
      closeDialog();
    },
  });

  const rename = useMutation({
    mutationFn: ({ playlist, value }: { playlist: PlaylistRecord; value: PlaylistFormValue }) =>
      updatePlaylist(playlist.playlistId, {
        name: value.name,
        storeId: playlist.storeId,
        brands: playlist.storeId === null ? value.brands : [],
        items: playlist.items,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PLAYLISTS_QUERY_KEY });
      closeDialog();
    },
  });

  const remove = useMutation({
    mutationFn: (playlist: PlaylistRecord) =>
      deletePlaylist(playlist.storeId, playlist.playlistId),
    onSuccess: async (_result, playlist) => {
      await queryClient.invalidateQueries({ queryKey: PLAYLISTS_QUERY_KEY });
      if (selectedId === playlist.playlistId) setSelectedId(null);
      closeDialog();
    },
  });

  // Deliberately memoised on primitives rather than on `selected`. The preview
  // builds a manifest from this object and re-resolves whenever its identity
  // changes; if that identity changed on every render, the resolver would run on
  // every render and the render it triggers would trigger the next one.
  const selectedName = selected?.name ?? '';
  const previewDraft: DraftPreviewSource | null = useMemo(() => {
    if (selectedId === null || draftRows === undefined) return null;
    return {
      playlistId: selectedId,
      playlistName: selectedName,
      items: draftItems,
      assets,
      storeId: scopeStoreId,
    };
  }, [selectedId, selectedName, scopeStoreId, draftRows, draftItems, assets]);

  const addAssets = (chosen: readonly MediaAsset[]): void => {
    if (draftRows !== undefined) setDraftRows([...draftRows, ...chosen.map(draftItemFor)]);
    closeDialog();
  };

  return (
    <>
      <PageHeader
        title="Playlists"
        description="An ordered loop of media, with the playback settings that make it look right on the wall."
      >
        <button
          type="button"
          className="button button--primary"
          onClick={() => {
            create.reset();
            setDialog({ kind: 'create' });
          }}
          disabled={writableStores.length === 0 && !isAdmin(user)}
        >
          New playlist
        </button>
      </PageHeader>

      <InfoNote>
        A playlist is saved as one document, so a reorder is a single atomic write and a TV fetching
        its manifest mid-edit gets the old list or the new one, never half of each.
      </InfoNote>

      {playlists.isPending ? <LoadingBlock label="Loading playlists…" /> : null}

      {playlists.isError ? (
        <ErrorBlock
          error={playlists.error}
          action="load the playlists"
          onRetry={() => void playlists.refetch()}
        />
      ) : null}

      {!playlists.isPending && !playlists.isError && rows.length === 0 ? (
        hasNoScope(user) ? (
          <InlineEmpty
            title="No stores assigned to you yet"
            description="Playlists belong to a store, and your account has not been given one. An administrator needs to add you before you can build a loop."
          />
        ) : (
          <InlineEmpty
            title="No playlists yet"
            description="A playlist is an ordered list of media with per-item settings. Build one here, then set it as a screen's everyday loop."
          />
        )
      ) : null}

      {rows.length > 0 ? (
        <section className="panel">
          <div className="panel__head">
            <div>
              <h3 className="panel__title">{rows.length === 1 ? '1 playlist' : `${rows.length} playlists`}</h3>
              <p className="panel__subtitle">
                Choose one to edit its items, its timings and how each one fills the screen.
              </p>
            </div>
          </div>

          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Playlist</th>
                  <th scope="col">Items</th>
                  <th scope="col">Last changed</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.playlistId}
                    className={row.playlistId === selectedId ? 'is-selected' : undefined}
                  >
                    <th scope="row" className="data-table__name">
                      <button
                        type="button"
                        className="button button--link"
                        onClick={() => {
                          // A failed save belongs to the playlist it was for; it
                          // must not follow somebody onto the next one.
                          save.reset();
                          setSelectedId(row.playlistId);
                        }}
                      >
                        {row.name}
                      </button>
                      <span className="data-table__secondary">{scopeLabel(row, stores.data ?? [])}</span>
                    </th>
                    <td>{row.items.length === 1 ? '1 item' : `${row.items.length} items`}</td>
                    <td>{formatRelativeTime(row.updatedAt ?? null, now)}</td>
                    <td className="data-table__actions">
                      <button
                        type="button"
                        className="button button--ghost button--small"
                        disabled={!canWrite(row)}
                        onClick={() => {
                          rename.reset();
                          setDialog({ kind: 'rename', playlist: row });
                        }}
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        className="button button--ghost button--small"
                        disabled={!canWrite(row)}
                        onClick={() => {
                          remove.reset();
                          setDialog({ kind: 'delete', playlist: row });
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {selected !== null && draftRows !== undefined ? (
        <PlaylistEditor
          playlist={selected}
          assets={assets}
          assetsError={pickerMedia.error}
          items={draftRows}
          onItemsChange={setDraftRows}
          dirty={dirty}
          saving={save.isPending}
          saveError={save.error}
          readOnly={!writable}
          onSave={() => save.mutate({ playlist: selected, items: [...draftItems] })}
          onRevert={() => setDraftRows(toDraftItems(selected.items))}
          onAddMedia={() => setDialog({ kind: 'picker' })}
        />
      ) : null}

      <ScreenPreviewPanel draft={previewDraft} />

      <MediaPicker
        open={dialog.kind === 'picker'}
        assets={assets}
        loading={pickerMedia.isFetching}
        error={pickerMedia.error}
        onAdd={addAssets}
        onClose={closeDialog}
      />

      <Modal
        open={dialog.kind === 'create' || dialog.kind === 'rename'}
        title={dialog.kind === 'rename' ? `Rename ${dialog.playlist.name}` : 'New playlist'}
        onClose={closeDialog}
      >
        {dialog.kind === 'create' ? (
          <PlaylistForm
            mode="create"
            initialName=""
            initialStoreId={writableStores[0]?.storeId ?? null}
            initialBrands={[]}
            stores={writableStores}
            canWriteShared={isAdmin(user)}
            busy={create.isPending}
            error={create.error}
            onSubmit={(value) => create.mutate(value)}
            onCancel={closeDialog}
          />
        ) : null}

        {dialog.kind === 'rename' ? (
          <PlaylistForm
            mode="rename"
            initialName={dialog.playlist.name}
            initialStoreId={dialog.playlist.storeId}
            initialBrands={dialog.playlist.brands}
            stores={writableStores}
            canWriteShared={isAdmin(user)}
            busy={rename.isPending}
            error={rename.error}
            onSubmit={(value) => rename.mutate({ playlist: dialog.playlist, value })}
            onCancel={closeDialog}
          />
        ) : null}
      </Modal>

      <ConfirmDialog
        open={dialog.kind === 'delete'}
        title={dialog.kind === 'delete' ? `Delete ${dialog.playlist.name}?` : 'Delete playlist?'}
        description="The media itself is untouched — only this ordering of it goes. Any screen using it as its everyday playlist falls back to its idle card until another one is assigned."
        confirmLabel="Delete playlist"
        busyLabel="Deleting…"
        busy={remove.isPending}
        error={remove.error === null ? null : errorMessage(remove.error)}
        onConfirm={() => {
          if (dialog.kind === 'delete') remove.mutate(dialog.playlist);
        }}
        onClose={closeDialog}
      />
    </>
  );
}

function sortPlaylists(rows: readonly PlaylistRecord[]): PlaylistRecord[] {
  return [...rows].sort((left, right) => left.name.localeCompare(right.name));
}

/** Store playlists come first in their own store's words; shared ones say so. */
function scopeLabel(
  playlist: PlaylistRecord,
  stores: readonly { storeId: string; name: string }[],
): string {
  if (playlist.storeId === null) {
    return playlist.brands.length === 0
      ? 'Shared across every store'
      : `Shared · ${playlist.brands.join(', ')}`;
  }
  return stores.find((store) => store.storeId === playlist.storeId)?.name ?? playlist.storeId;
}

